import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import {
  Mail,
  Plus,
  Trash2,
  Star,
  StarOff,
  Send,
  Eye,
  RefreshCw,
  TestTube,
  Clock,
  Server,
  Save,
} from "lucide-react";

// Variables disponibles en las plantillas
const TEMPLATE_VARS = [
  { var: "{{nombre}}", desc: "Nombre del cliente" },
  { var: "{{tipo_evento}}", desc: "Tipo de evento" },
  { var: "{{fecha_evento}}", desc: "Fecha del evento" },
  { var: "{{ubicacion}}", desc: "Ubicación" },
  { var: "{{telefono}}", desc: "Teléfono" },
  { var: "{{email_cliente}}", desc: "Email del cliente" },
  { var: "{{notas}}", desc: "Notas" },
];

const DEFAULT_TEMPLATE_HTML = `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <h2 style="color: #6366f1;">Hola, {{nombre}} 👋</h2>
  <p>Nos ponemos en contacto contigo respecto al fotomatón para tu <strong>{{tipo_evento}}</strong>.</p>
  <p>Hemos visto que tienes una consulta pendiente para el día <strong>{{fecha_evento}}</strong> en <strong>{{ubicacion}}</strong>.</p>
  <p>¿Tienes alguna pregunta o quieres confirmar más detalles? No dudes en contactarnos.</p>
  <br>
  <p>Un saludo,<br><strong>Fotomatón Costa Brava</strong></p>
</div>`;

export default function EmailsTab() {
  const [templates, setTemplates] = useState([]);
  const [emailConfig, setEmailConfig] = useState({
    smtp_host: "", smtp_port: 587, smtp_user: "", smtp_password: "",
    from_name: "Fotomatón Costa Brava", use_ssl: false, bcc_email: "",
    send_hour: 10, send_minute: 0, habilitado: true
  });
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await api.getEmailTemplates();
      setTemplates(res.data);
      if (res.data.length > 0 && !selectedTemplate) {
        const defTemplate = res.data.find(t => t.es_defecto) || res.data[0];
        setSelectedTemplate(defTemplate);
      }
    } catch (e) { console.error(e); }
  }, [selectedTemplate]);

  const loadConfig = useCallback(async () => {
    try {
      const res = await api.getEmailConfig();
      setEmailConfig(prev => ({ ...prev, ...res.data }));
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    loadTemplates();
    loadConfig();
  }, [loadTemplates, loadConfig]);

  const handleSaveConfig = async () => {
    setLoadingConfig(true);
    try {
      await api.updateEmailConfig(emailConfig);
      toast.success("Configuración SMTP guardada");
    } catch (e) {
      toast.error("Error al guardar configuración");
    } finally { setLoadingConfig(false); }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const res = await api.testEmailConfig();
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (e) {
      toast.error("Error al enviar email de prueba. Revisa la configuración.");
    } finally { setTestingEmail(false); }
  };

  const handleNewTemplate = () => {
    setEditingTemplate({
      id: null, nombre: "", asunto: "Hola {{nombre}}, te contactamos sobre tu consulta",
      cuerpo_html: DEFAULT_TEMPLATE_HTML, es_defecto: templates.length === 0
    });
    setShowPreview(false);
  };

  const handleEditTemplate = (t) => {
    setEditingTemplate({ ...t });
    setSelectedTemplate(t);
    setShowPreview(false);
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate.nombre || !editingTemplate.asunto || !editingTemplate.cuerpo_html) {
      toast.error("Rellena nombre, asunto y cuerpo del email");
      return;
    }
    setLoadingTemplates(true);
    try {
      if (editingTemplate.id) {
        const res = await api.updateEmailTemplate(editingTemplate.id, editingTemplate);
        setSelectedTemplate(res.data);
        toast.success("Plantilla actualizada");
      } else {
        const res = await api.createEmailTemplate(editingTemplate);
        setSelectedTemplate(res.data);
        toast.success("Plantilla creada");
      }
      setEditingTemplate(null);
      await loadTemplates();
    } catch (e) {
      toast.error("Error al guardar plantilla");
    } finally { setLoadingTemplates(false); }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      await api.deleteEmailTemplate(id);
      toast.success("Plantilla eliminada");
      setSelectedTemplate(null);
      setEditingTemplate(null);
      await loadTemplates();
    } catch (e) { toast.error("Error al eliminar"); }
  };

  const handleSetDefault = async (id) => {
    try {
      await api.setDefaultEmailTemplate(id);
      toast.success("Plantilla establecida como predeterminada");
      await loadTemplates();
    } catch (e) { toast.error("Error"); }
  };

  const handlePreview = () => {
    const src = editingTemplate?.cuerpo_html || selectedTemplate?.cuerpo_html || "";
    const example = {
      "{{nombre}}": "María García",
      "{{tipo_evento}}": "Boda",
      "{{fecha_evento}}": "2025-08-15",
      "{{ubicacion}}": "Mas Llagostera, Tossa de Mar",
      "{{telefono}}": "600 123 456",
      "{{email_cliente}}": "maria@ejemplo.com",
      "{{notas}}": "",
    };
    let rendered = src;
    Object.entries(example).forEach(([k, v]) => { rendered = rendered.replaceAll(k, v); });
    setPreviewHtml(rendered);
    setShowPreview(true);
  };

  const insertVar = (variable) => {
    if (!editingTemplate) return;
    setEditingTemplate(prev => ({ ...prev, cuerpo_html: prev.cuerpo_html + variable }));
  };

  return (
    <div className="space-y-6">
      {/* SMTP Config */}
      <Card className="border-0 shadow-soft">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Server className="w-5 h-5" />
            Configuración SMTP
          </CardTitle>
          <CardDescription>
            Credenciales del servidor de correo. Con Gmail usa una <strong>App Password</strong> (no tu contraseña normal).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2 space-y-2">
              <Label>Servidor SMTP</Label>
              <Input placeholder="smtp.strato.de" value={emailConfig.smtp_host}
                onChange={e => setEmailConfig(p => ({ ...p, smtp_host: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Puerto</Label>
              <Input type="number" placeholder="465" value={emailConfig.smtp_port}
                onChange={e => setEmailConfig(p => ({ ...p, smtp_port: parseInt(e.target.value) || 465 }))} />
            </div>
          </div>
          <div className="flex items-center gap-3 py-1">
            <Switch
              checked={!!emailConfig.use_ssl}
              onCheckedChange={v => setEmailConfig(p => ({ ...p, use_ssl: v, smtp_port: v ? 465 : 587 }))}
            />
            <div>
              <Label className="cursor-pointer">Usar SSL/TLS</Label>
              <p className="text-xs text-muted-foreground">
                {emailConfig.use_ssl
                  ? "SSL/TLS directo (puerto 465) — Strato, Ionos, algunos Outlook"
                  : "STARTTLS (puerto 587) — Gmail, la mayoría de proveedores"}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Usuario (email remitente)</Label>
              <Input placeholder="tu@gmail.com" value={emailConfig.smtp_user}
                onChange={e => setEmailConfig(p => ({ ...p, smtp_user: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Contraseña / App Password</Label>
              <Input type="password" placeholder="••••••••••••••••" value={emailConfig.smtp_password}
                onChange={e => setEmailConfig(p => ({ ...p, smtp_password: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nombre remitente (aparece en el email)</Label>
              <Input placeholder="Fotomatón Costa Brava" value={emailConfig.from_name}
                onChange={e => setEmailConfig(p => ({ ...p, from_name: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Copia oculta (Bcc)</Label>
              <Input placeholder="jordi@fotomatoncb.es" value={emailConfig.bcc_email || ""}
                onChange={e => setEmailConfig(p => ({ ...p, bcc_email: e.target.value }))} />
              <p className="text-xs text-muted-foreground">Recibirás copia de todos los emails enviados</p>
            </div>
          </div>

          {/* Scheduler */}
          <div className="pt-4 border-t border-border/40">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" />
                <span className="font-medium">Envío automático diario</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{emailConfig.habilitado ? "Activado" : "Desactivado"}</span>
                <Switch checked={emailConfig.habilitado}
                  onCheckedChange={v => setEmailConfig(p => ({ ...p, habilitado: v }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 max-w-xs">
              <div className="space-y-2">
                <Label>Hora (0-23)</Label>
                <Input type="number" min="0" max="23" value={emailConfig.send_hour}
                  onChange={e => setEmailConfig(p => ({ ...p, send_hour: parseInt(e.target.value) || 0 }))} />
              </div>
              <div className="space-y-2">
                <Label>Minuto (0-59)</Label>
                <Input type="number" min="0" max="59" value={emailConfig.send_minute}
                  onChange={e => setEmailConfig(p => ({ ...p, send_minute: parseInt(e.target.value) || 0 }))} />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              El scheduler enviará emails automáticamente a las{" "}
              <strong>{String(emailConfig.send_hour).padStart(2, "0")}:{String(emailConfig.send_minute).padStart(2, "0")}h</strong>{" "}
              a todas las consultas con esa fecha como "próximo contacto".
            </p>
          </div>

          <div className="flex gap-2 pt-2">
            <Button onClick={handleSaveConfig} disabled={loadingConfig} className="rounded-full gap-2">
              <Save className="w-4 h-4" />
              {loadingConfig ? "Guardando..." : "Guardar configuración"}
            </Button>
            <Button variant="outline" onClick={handleTestEmail} disabled={testingEmail} className="rounded-full gap-2">
              <TestTube className="w-4 h-4" />
              {testingEmail ? "Enviando..." : "Enviar email de prueba"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Templates */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Lista */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <Mail className="w-4 h-4" /> Plantillas
            </h3>
            <Button size="sm" onClick={handleNewTemplate} className="rounded-full gap-1 h-8">
              <Plus className="w-3 h-3" /> Nueva
            </Button>
          </div>
          {templates.length === 0 && (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No hay plantillas. Crea una para empezar.
            </p>
          )}
          {templates.map(t => (
            <Card
              key={t.id}
              className={`border cursor-pointer transition-all hover:shadow-md ${selectedTemplate?.id === t.id ? "border-primary bg-primary/5" : "border-border/60"}`}
              onClick={() => handleEditTemplate(t)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{t.nombre}</p>
                    <p className="text-xs text-muted-foreground truncate">{t.asunto}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {t.es_defecto ? (
                      <Badge className="text-xs bg-yellow-100 text-yellow-700 border-yellow-200">
                        <Star className="w-3 h-3 mr-1" /> Defecto
                      </Badge>
                    ) : (
                      <Button variant="ghost" size="icon" className="h-7 w-7" title="Establecer como defecto"
                        onClick={e => { e.stopPropagation(); handleSetDefault(t.id); }}>
                        <StarOff className="w-3 h-3" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:bg-destructive/10"
                      onClick={e => { e.stopPropagation(); handleDeleteTemplate(t.id); }}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Editor */}
        <div className="lg:col-span-2">
          {editingTemplate ? (
            <Card className="border-0 shadow-soft">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">
                  {editingTemplate.id ? `Editando: ${editingTemplate.nombre}` : "Nueva plantilla"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nombre de la plantilla</Label>
                    <Input placeholder="Ej: Seguimiento inicial"
                      value={editingTemplate.nombre}
                      onChange={e => setEditingTemplate(p => ({ ...p, nombre: e.target.value }))} />
                  </div>
                  <div className="flex items-center gap-3 pt-7">
                    <Switch checked={editingTemplate.es_defecto}
                      onCheckedChange={v => setEditingTemplate(p => ({ ...p, es_defecto: v }))} />
                    <Label>Plantilla por defecto</Label>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Asunto del email</Label>
                  <Input placeholder="Ej: Te contactamos sobre tu consulta, {{nombre}}"
                    value={editingTemplate.asunto}
                    onChange={e => setEditingTemplate(p => ({ ...p, asunto: e.target.value }))} />
                </div>

                {/* Variables disponibles */}
                <div>
                  <Label className="mb-2 block">Variables disponibles (clic para insertar)</Label>
                  <div className="flex flex-wrap gap-2">
                    {TEMPLATE_VARS.map(v => (
                      <button key={v.var}
                        onClick={() => insertVar(v.var)}
                        title={v.desc}
                        className="px-2 py-1 text-xs font-mono bg-muted hover:bg-primary/10 rounded border border-border/60 transition-colors">
                        {v.var}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Cuerpo HTML del email</Label>
                  <Textarea
                    value={editingTemplate.cuerpo_html}
                    onChange={e => setEditingTemplate(p => ({ ...p, cuerpo_html: e.target.value }))}
                    className="font-mono text-xs rounded-xl resize-none min-h-[250px]"
                    placeholder="<div>Tu HTML aquí...</div>"
                  />
                </div>

                <div className="flex gap-2 flex-wrap">
                  <Button onClick={handleSaveTemplate} disabled={loadingTemplates} className="rounded-full gap-2">
                    <Save className="w-4 h-4" />
                    {loadingTemplates ? "Guardando..." : "Guardar plantilla"}
                  </Button>
                  <Button variant="outline" onClick={handlePreview} className="rounded-full gap-2">
                    <Eye className="w-4 h-4" /> Preview
                  </Button>
                  <Button variant="ghost" onClick={() => { setEditingTemplate(null); setShowPreview(false); }} className="rounded-full">
                    Cancelar
                  </Button>
                </div>

                {/* Preview incrustado */}
                {showPreview && (
                  <div className="border border-border rounded-xl overflow-hidden">
                    <div className="bg-muted/50 px-4 py-2 border-b text-sm font-medium flex items-center gap-2">
                      <Eye className="w-4 h-4" /> Preview (datos de ejemplo)
                    </div>
                    <iframe
                      srcDoc={previewHtml}
                      title="Preview email"
                      className="w-full min-h-[300px] border-0"
                      sandbox="allow-same-origin"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="flex items-center justify-center h-full min-h-[200px] border-2 border-dashed border-border/40 rounded-xl text-muted-foreground">
              <div className="text-center">
                <Mail className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">Selecciona una plantilla para editarla</p>
                <p className="text-xs mt-1">o crea una nueva con el botón "Nueva"</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
